/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../../logger';
import type {
  DiscoveredUnit,
  DiscoveryConfig,
  PlacementIdConfig,
} from '../../types/advertorial';
import { UnitIdGenerator } from './placement';

const logger = Logger.getInstance();

/**
 * Discovery result callback
 */
export type DiscoveryCallback = (units: DiscoveredUnit[]) => void;

/**
 * Discovers advertorial units in the DOM based on CSS selectors
 */
export class AdvertorialDiscovery {
  private readonly config: DiscoveryConfig;
  private readonly selectors: string[];
  private readonly placementIdGenerator: UnitIdGenerator;
  private containerObserver: MutationObserver | null = null;
  private handledContainers: Set<HTMLElement> = new Set();
  private deliveredContainers: Set<HTMLElement> = new Set();
  private unitWatchers: Map<
    HTMLElement,
    { observer: MutationObserver | null; loadHandler: (() => void) | null }
  > = new Map();
  private pendingTimeouts: Set<ReturnType<typeof setTimeout>> = new Set();
  private globalUnitIndex: number = 0;
  private customEventHandler: ((event: Event) => void) | null = null;
  private discoveredIds: Set<string> = new Set();

  constructor(config: DiscoveryConfig, placementIdConfig: PlacementIdConfig) {
    this.config = config;
    this.selectors = config.selectors ?? [];
    this.placementIdGenerator = new UnitIdGenerator(placementIdConfig);
  }

  /**
   * Start discovery based on configured timing
   * @param callback Function to call with discovered units
   */
  public start(callback: DiscoveryCallback): void {
    const { timing, delay } = this.config;

    switch (timing) {
      case 'immediate':
        this.discover(callback);
        break;

      case 'dom-ready':
        this.onDomReady(() => this.discover(callback));
        break;

      case 'load':
        this.onLoad(() => this.discover(callback));
        break;

      case 'delayed':
        this.onDomReady(() => {
          setTimeout(() => this.discover(callback), delay || 0);
        });
        break;

      case 'observe':
        this.observeForContainer(callback, delay);
        break;

      case 'custom-event':
        this.startCustomEventListener(callback);
        break;
    }
  }

  /**
   * Perform discovery immediately
   * @returns Array of discovered units
   */
  public discoverNow(): DiscoveredUnit[] {
    const units: DiscoveredUnit[] = [];

    // Track seen elements to avoid duplicates
    const seenElements = new Set<HTMLElement>();

    for (const selector of this.selectors) {
      try {
        const elements = document.querySelectorAll<HTMLElement>(selector);

        for (const element of Array.from(elements)) {
          // Skip if already found by another selector
          if (seenElements.has(element)) {
            continue;
          }

          seenElements.add(element);

          // Generate placement ID
          const placementIdResult = this.placementIdGenerator.generate(element, units.length);

          units.push({
            element,
            selector,
            placementId: placementIdResult.id,
          });
        }
      } catch (e) {
        // Invalid selector or other error, skip and continue
        logger.debug(`Failed to query selector "${selector}": ${e}`);
      }
    }

    return units;
  }

  /**
   * Discover units and invoke callback
   */
  private discover(callback: DiscoveryCallback): void {
    try {
      const units = this.discoverNow();
      callback(units);
    } catch (e) {
      logger.error(`Error during advertorial discovery: ${e}`);
      callback([]);
    }
  }

  private onDomReady(callback: () => void): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  private onLoad(callback: () => void): void {
    if (document.readyState === 'complete') {
      // Page is already loaded
      callback();
    } else {
      window.addEventListener('load', callback, { once: true });
    }
  }

  /**
   * Observe the DOM for container elements and discover units within each.
   * Multiple containers are supported (e.g. several sponsored-content zones on
   * one page): every element matching the container selector — whether present
   * at start or added later — is handled independently, and the callback fires
   * once per container with that container's units.
   */
  private observeForContainer(callback: DiscoveryCallback, delay?: number): void {
    // Clean up any existing observer to prevent leaks if called multiple times
    this.stopObserving();

    if (this.selectors.length === 0) {
      logger.debug('No selectors configured for observe timing');
      callback([]);
      return;
    }

    const containerSelector = this.selectors[0];

    const handleNewContainer = (container: HTMLElement): void => {
      if (this.handledContainers.has(container)) {
        return;
      }
      this.handledContainers.add(container);
      this.handleContainerDetected(container, callback, delay);
    };

    // Handle any containers already in the DOM
    try {
      const existing = document.querySelectorAll<HTMLElement>(containerSelector);
      if (existing.length > 0) {
        logger.debug(`${existing.length} container(s) already exist: ${containerSelector}`);
        existing.forEach(handleNewContainer);
      }
    } catch (e) {
      logger.debug(`Failed to query container selector "${containerSelector}": ${e}`);
      callback([]);
      return;
    }

    // Keep watching for further containers appearing later
    if (typeof MutationObserver === 'undefined') {
      if (this.handledContainers.size === 0) {
        logger.warn('MutationObserver not available, falling back to immediate discovery');
        this.discover(callback);
      }
      return;
    }

    // Wait for body to exist before attaching observer
    if (!document.body) {
      this.onDomReady(() => this.observeForContainer(callback, delay));
      return;
    }

    logger.debug(`Observing for containers: ${containerSelector}`);

    this.containerObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          for (const node of Array.from(mutation.addedNodes)) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement;

              // Check if added node matches container selector
              if (element.matches?.(containerSelector)) {
                handleNewContainer(element);
              }

              // Check for containers within the added node
              const nested = element.querySelectorAll?.<HTMLElement>(containerSelector);
              if (nested && nested.length > 0) {
                nested.forEach(handleNewContainer);
              }
            }
          }
        }
      }
    });

    this.containerObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Handle container detection - discover units within it
   */
  private handleContainerDetected(
    container: HTMLElement,
    callback: DiscoveryCallback,
    delay?: number,
  ): void {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const runDiscovery = () => {
      if (timeout !== null) {
        this.pendingTimeouts.delete(timeout);
      }
      const units = this.discoverWithinContainer(container);
      if (units.length > 0) {
        this.deliveredContainers.add(container);
        callback(units);
        return;
      }
      // Container found but no units yet — e.g. a GPT friendly iframe is
      // created before the creative writes its markup, or the provider fills
      // the container asynchronously. Keep watching until the unit selectors
      // match instead of giving up after a single one-shot query.
      this.observeForUnits(container, callback);
    };

    if (delay && delay > 0) {
      timeout = setTimeout(runDiscovery, delay);
      this.pendingTimeouts.add(timeout);
    } else {
      runDiscovery();
    }
  }

  /**
   * Resolve the root node to query unit selectors against. When the container
   * is a same-origin iframe (e.g. a GAM friendly iframe hosting the sponsored
   * content), unit selectors run against the iframe's document. Returns null
   * when the iframe document is inaccessible (cross-origin / SafeFrame).
   */
  private resolveContainerRoot(
    container: HTMLElement,
  ): { root: HTMLElement | Document; iframe: HTMLIFrameElement | null } | null {
    if (container.tagName !== 'IFRAME') {
      return { root: container, iframe: null };
    }

    const iframe = container as HTMLIFrameElement;
    let doc: Document | null = null;
    try {
      doc = iframe.contentDocument;
    } catch (e) {
      doc = null;
    }

    if (!doc) {
      logger.warn(
        `Advertorial container "${this.selectors[0]}" is an iframe with an inaccessible document (cross-origin/SafeFrame) — cannot discover within it`,
      );
      return null;
    }

    return { root: doc, iframe };
  }

  /**
   * Watch the container's root for unit-selector matches after an initial
   * discovery found nothing. For iframe containers the observer attaches to
   * the iframe's current document; a `load` listener re-attaches when GPT
   * replaces that document via document.write.
   */
  private observeForUnits(container: HTMLElement, callback: DiscoveryCallback): void {
    this.stopUnitWatcher(container);

    const unitSelectors = this.selectors.slice(1);
    if (unitSelectors.length === 0 || typeof MutationObserver === 'undefined') {
      callback([]);
      return;
    }

    const watcher: { observer: MutationObserver | null; loadHandler: (() => void) | null } = {
      observer: null,
      loadHandler: null,
    };
    this.unitWatchers.set(container, watcher);

    const tryDiscover = (): boolean => {
      if (this.deliveredContainers.has(container)) {
        return true;
      }
      const units = this.discoverWithinContainer(container);
      if (units.length > 0) {
        this.deliveredContainers.add(container);
        this.stopUnitWatcher(container);
        callback(units);
        return true;
      }
      return false;
    };

    const matchesUnitSelector = (el: HTMLElement): boolean =>
      unitSelectors.some((s) => {
        try {
          return el.matches?.(s) || el.querySelector?.(s) !== null;
        } catch (e) {
          return false;
        }
      });

    const attach = (): void => {
      const resolved = this.resolveContainerRoot(container);
      if (!resolved) {
        return; // inaccessible iframe doc; the load listener may succeed later
      }
      watcher.observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) {
            continue;
          }
          for (const node of Array.from(mutation.addedNodes)) {
            if (node.nodeType === Node.ELEMENT_NODE && matchesUnitSelector(node as HTMLElement)) {
              tryDiscover();
              return;
            }
          }
        }
      });
      watcher.observer.observe(resolved.root, { childList: true, subtree: true });
    };

    attach();

    if (container.tagName === 'IFRAME') {
      // GPT friendly iframes replace their initial about:blank document via
      // document.open()/write(); observers attached to the old document die
      // silently, so re-check and re-attach whenever the iframe (re)loads.
      watcher.loadHandler = () => {
        if (!tryDiscover()) {
          if (watcher.observer) {
            watcher.observer.disconnect();
            watcher.observer = null;
          }
          attach();
        }
      };
      container.addEventListener('load', watcher.loadHandler);
    }

    logger.debug('Container found but no advertorial units yet; observing for unit selectors');
  }

  /**
   * Stop the unit watcher for one container
   */
  private stopUnitWatcher(container: HTMLElement): void {
    const watcher = this.unitWatchers.get(container);
    if (!watcher) {
      return;
    }
    if (watcher.observer) {
      watcher.observer.disconnect();
      watcher.observer = null;
    }
    if (watcher.loadHandler) {
      container.removeEventListener('load', watcher.loadHandler);
      watcher.loadHandler = null;
    }
    this.unitWatchers.delete(container);
  }

  /**
   * Stop all per-container unit watchers
   */
  private stopObservingUnits(): void {
    for (const container of Array.from(this.unitWatchers.keys())) {
      this.stopUnitWatcher(container);
    }
  }

  /**
   * Discover units within a specific container element
   */
  private discoverWithinContainer(container: HTMLElement): DiscoveredUnit[] {
    const units: DiscoveredUnit[] = [];

    // Use remaining selectors (skip first which is container)
    const unitSelectors = this.selectors.slice(1);

    // If no unit selectors, treat container as single unit
    if (unitSelectors.length === 0) {
      logger.debug('No unit selectors, using container as single unit');
      const placementIdResult = this.placementIdGenerator.generate(container, this.globalUnitIndex, {
        container,
        containerIndex: 0,
      });
      this.globalUnitIndex++;
      return [
        {
          element: container,
          selector: this.selectors[0],
          placementId: placementIdResult.id,
        },
      ];
    }

    // When the container is a same-origin iframe, query within its document
    const resolved = this.resolveContainerRoot(container);
    if (!resolved) {
      return [];
    }

    const seenElements = new Set<HTMLElement>();

    for (const selector of unitSelectors) {
      try {
        const elements = resolved.root.querySelectorAll<HTMLElement>(selector);

        for (const element of Array.from(elements)) {
          if (seenElements.has(element)) continue;
          seenElements.add(element);

          // Global index keeps 'position' IDs unique across multiple
          // containers on one page; containerIndex scopes 'container-id' IDs.
          const placementIdResult = this.placementIdGenerator.generate(
            element,
            this.globalUnitIndex + units.length,
            { container, containerIndex: units.length },
          );
          units.push({
            element,
            selector,
            placementId: placementIdResult.id,
          });
        }
      } catch (e) {
        logger.debug(`Failed to query selector "${selector}": ${e}`);
      }
    }

    this.globalUnitIndex += units.length;
    return units;
  }

  /**
   * Stop observing for container element
   */
  private stopObserving(): void {
    if (this.containerObserver) {
      this.containerObserver.disconnect();
      this.containerObserver = null;
    }
  }

  /**
   * Get a nested property from an object using pre-split keys
   */
  private getNestedValue(obj: unknown, keys: string[]): unknown {
    let current: unknown = obj;
    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = (current as Record<string, unknown>)[key];
      } else {
        return undefined;
      }
    }
    return current;
  }

  /**
   * Escape a string for use in CSS selectors
   * Falls back to a simple escape if CSS.escape is not available
   */
  private escapeCssSelector(str: string): string {
    if (typeof CSS !== 'undefined' && CSS.escape) {
      return CSS.escape(str);
    }
    // Simple fallback: escape special characters
    return str.replace(/([!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1');
  }

  /**
   * Start listening for custom DOM events to trigger discovery
   */
  private startCustomEventListener(callback: DiscoveryCallback): void {
    // Clean up any existing listener to prevent leaks if called multiple times
    this.stopCustomEventListener();

    const { event: eventConfig } = this.config;
    if (!eventConfig) return;

    // Pre-split dpath once to avoid per-event allocations
    const dpathKeys = eventConfig.dpath.split('.');

    this.customEventHandler = (domEvent: Event) => {
      const customEvt = domEvent as CustomEvent;
      const id = this.getNestedValue(customEvt.detail, dpathKeys);

      if (!id || typeof id !== 'string' || this.discoveredIds.has(id)) return;

      // Build selector and query element - wrapped in try/catch because:
      // 1. CSS.escape fallback is incomplete (leading digits, whitespace in id)
      // 2. eventConfig.attribute could contain characters that break selector syntax
      let element: HTMLElement | null;
      let selector: string;

      try {
        // Use attribute selector syntax for all attributes (more robust than #id)
        const escapedId = this.escapeCssSelector(id);
        selector = `[${eventConfig.attribute}="${escapedId}"]`;
        element = document.querySelector<HTMLElement>(selector);
      } catch (e) {
        logger.debug(`Failed to query element for custom event: ${e}`);
        return;
      }

      if (!element) {
        logger.debug(
          `Custom event received but no element found with ${eventConfig.attribute}="${id}"`,
        );
        return;
      }

      this.discoveredIds.add(id);
      const placementIdResult = this.placementIdGenerator.generate(
        element,
        this.discoveredIds.size - 1,
      );

      logger.debug(`Custom event discovered unit: ${id}`);

      callback([
        {
          element,
          selector,
          placementId: placementIdResult.id,
        },
      ]);
    };

    // Add listener to both document and window - publishers may dispatch on either
    // Deduplication via discoveredIds prevents processing the same ID twice
    document.addEventListener(eventConfig.type, this.customEventHandler);
    window.addEventListener(eventConfig.type, this.customEventHandler);
    logger.debug(`Listening for custom event: ${eventConfig.type} (on document and window)`);
  }

  /**
   * Stop listening for custom events
   */
  private stopCustomEventListener(): void {
    if (this.customEventHandler && this.config.event) {
      document.removeEventListener(this.config.event.type, this.customEventHandler);
      window.removeEventListener(this.config.event.type, this.customEventHandler);
      this.customEventHandler = null;
    }
  }

  /**
   * Clean up observer and pending timers if discovery is stopped externally
   */
  public stop(): void {
    this.stopObserving();
    this.stopObservingUnits();
    this.stopCustomEventListener();
    this.discoveredIds.clear();
    this.handledContainers.clear();
    this.deliveredContainers.clear();

    for (const timeout of this.pendingTimeouts) {
      clearTimeout(timeout);
    }
    this.pendingTimeouts.clear();
  }
}
